import React from 'react';

/**
 * ConceptMatchToggle — 개념 검색 상자 **안쪽**의 OR/AND(ConceptFilterInput 의 trailing 자리).
 * 문제(/problem-hub) 검색과 시험지 조립 창이 **같은 부품**을 쓴다 — 두 화면은 오너 지시(2026-08-29
 * 「그대로 적용」)로 검색 조작을 똑같이 맞춰 둔 형제라, 한쪽만 모양이나 뜻이 바뀌면 같은 버튼을 두 가지로 배운다.
 *
 * 값: 'any'(OR · 하나라도 포함) | 'all'(AND · 모두 포함).
 * AND 는 2026-09-13 에 풀렸다. 주소줄 검색(GET)의 concept_ids 는 여전히 「하나라도」뿐이고, 「모두」는 같은 주소의
 * 본문 검색(POST)의 식으로 나간다(problemApi.conceptAllExpr · 백엔드 dev 20cb46d).
 * 글자는 OR/AND 두 칸 그대로 둔다(오너 결정 D2) — 좁은 마스터에서 이 상자의 글자 자리가 130.9px 뿐이라
 * 강의 v2 의 「하나라도·모두·정확히」 세 칸이 들어가지 않는다. 뜻은 툴팁이 말한다.
 *
 * 켜진 칸은 **바탕만 초록**이고 글자는 먹색이다. 초록 글자로 두면 연초록 바탕(#4ABC85 10%) 위 2.19:1 로
 * 비텍스트 하한 3 도 못 넘는다 — 먹색이면 13.53:1. 꺼진 칸은 흰 바탕 회색 500(4.83:1)이다.
 * 굵기는 켜짐·꺼짐이 같다 — 상태는 바탕과 글자색으로만 말한다(굵기가 바뀌면 글자 폭이 흔들린다).
 *
 * @param {'any'|'all'} value
 * @param {function} onChange     (match) => void — 이미 켜진 칸을 누르면 부르지 않는다
 * @param {string|null} lockedReason 주면 AND 를 잠그고 **OR 로 보인다**(값이 'all' 이어도).
 *                                조립 창에서 강의 조건을 고른 동안이 그렇다(오너 결정 D3) — 그때 검색은 OR 로 나간다.
 */
export const CONCEPT_MATCH_TITLE = {
  any: 'OR — 고른 개념 중 하나라도 포함한 문제',
  all: 'AND — 고른 개념을 모두 포함한 문제',
};

const BASE = 'px-2 py-0.5 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary';

function ConceptMatchToggle({ value = 'any', onChange, lockedReason = null }) {
  const current = lockedReason ? 'any' : value;
  const cell = (m, label) => {
    const on = current === m;
    const locked = lockedReason != null && m === 'all';
    const tone = locked
      ? 'cursor-not-allowed bg-gray-50 text-gray-300'
      : on ? 'bg-primary/10 text-gray-800' : 'bg-white text-gray-500 hover:bg-gray-50 hover:text-gray-700';
    return (
      <button
        type="button"
        aria-pressed={on}
        disabled={locked}
        title={locked ? lockedReason : CONCEPT_MATCH_TITLE[m]}
        onClick={() => { if (!on && onChange) onChange(m); }}
        className={`${BASE} ${m === 'all' ? 'border-l' : ''} ${tone}`}
      >
        {label}
      </button>
    );
  };
  return (
    <div role="group" aria-label="개념 엮는 방식" className="flex shrink-0 overflow-hidden rounded-md border text-[11px]">
      {cell('any', 'OR')}
      {cell('all', 'AND')}
    </div>
  );
}

/** 개념을 이만큼 **넘게** AND 로 묶으면 알린다 — 강의 v2 의 W3(syllabusQuery.exprWarnings)와 같은 문턱 */
export const CONCEPT_ALL_WARN_OVER = 5;

/**
 * 「모두 포함」이 결과를 거의 못 내는 조합을 검색 **전에** 알린다(강의 v2 W3 와 같은 뜻 — 오류가 아니라 확인 권유).
 * 조건이 아니면 아무것도 그리지 않는다. count 는 고른 개념 수.
 */
export function ConceptAllHint({ match, count }) {
  if (match !== 'all' || count <= CONCEPT_ALL_WARN_OVER) return null;
  return (
    <p className="text-[11px] text-amber-700">
      개념 {count}개를 모두 가진 문제는 거의 없습니다 — 하나라도 가진 문제를 찾으려면 OR 로 두세요.
    </p>
  );
}

export default ConceptMatchToggle;
